import React from 'react';
import * as Relay from 'react-relay';
import { fromGlobalId } from 'graphql-relay';
import { defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router';
import urlMapping from '../app/URLMapping';
import FlowTestResultView from './FlowTestResultView';
import AcceptSnapshotMutation from './AcceptSnapshotMutation';
import SetExpectedOutputMutation from './SetExpectedOutputMutation';
import DeleteTestInvocationMutation from './DeleteTestInvocationMutation';
import UpdateEnvironmentStateMutation from './UpdateEnvironmentStateMutation';
import { notification } from './notification';
import { getPayloadErrorMessage } from './mutationPayload';
import {
  buildDisplayComputedDiffPayload,
  parseComputedDiffPayload,
  parseSnapshotOutputPayload,
  cleanSnapshotOutputForDisplay,
  stripPrivateSnapshotFields,
} from '../../common/snapshot-testing/computedDiffPayload';
import type { ComputedDiffWrite, ComputedDiffGroup, ComputedDiffPayload } from '../../common/snapshot-testing/computedDiffPayload';

const uuidRegex = /[0-9a-fA-F\-]{30,40}/;

const messages = defineMessages({
  conflictDetected: {
    id: 'snapshotTesting.conflictDetected',
    defaultMessage: 'Conflict detected. Please refresh and try again.',
  },
  acceptFailed: {
    id: 'snapshotTesting.acceptFailed',
    defaultMessage: 'Failed to accept snapshot. Please try again.',
  },
  setExpectedOutputFailed: {
    id: 'snapshotTesting.setExpectedOutputFailed',
    defaultMessage: 'Failed to set expected output. Please try again.',
  },
  updateSnapshotFailed: {
    id: 'snapshotTesting.updateSnapshotFailed',
    defaultMessage: 'Failed to update snapshot. Please try again.',
  },
  deleteFailed: {
    id: 'snapshotTesting.deleteFailed',
    defaultMessage: 'Failed to delete test invocation. Please try again.',
  },
  confirmDelete: {
    id: 'snapshotTesting.confirmDelete',
    defaultMessage: 'Are you sure you want to delete this test invocation?',
  },
});

// Define missing types locally
interface InjectedIntl {
  formatMessage: (messageDescriptor: any, values?: any) => string;
}

interface InjectedIntlProps {
  intl: InjectedIntl;
}

interface InjectedRouter {
  push: (path: string) => void;
  replace: (path: string) => void;
  go: (n: number) => void;
  goBack: () => void;
  goForward: () => void;
  setRouteLeaveHook: (route: any, hook: any) => Function;
  isActive: (path: string, indexOnly?: boolean) => boolean;
}

interface WithRouterProps {
  router: InjectedRouter;
  params: any;
  location: any;
  routes: any;
}

export { ComputedDiffWrite, ComputedDiffGroup, ComputedDiffPayload, parseComputedDiffPayload, parseSnapshotOutputPayload, cleanSnapshotOutputForDisplay, buildDisplayComputedDiffPayload } from '../../common/snapshot-testing/computedDiffPayload';

export function cleanWriteForDisplay(write: any): any {
  if (!write || typeof write !== 'object') {
    return write;
  }

  const {
    stepId, stepInfo, status, _writeKey, _writeKeyBase, headers, requestHeaders, responseHeaders,
    requestHash, requestHashSource,
    ...rest
  } = write;
  return stripPrivateSnapshotFields(rest);
}

interface TestRun {
  id: string;
  realId?: string;
  status: string;
  createdAt: string;
  actualOutput: any;
  computedDiff?: any;
  error?: string;
  duration?: number;
  expiresIn?: number;
}

interface TestInvocation {
  id: string;
  realId: string;
  name: string;
  organisation_id: string;
  flow_id: string;
  flowDef_id?: string;
  flowDefVersion_id?: string;
  sourceFlowInvocation_id?: string;
  expectedOutput: any;
  version: number;
  createdAt?: string;
  updatedAt?: string;
  lastStatus?: string;
  lastRunAt?: string;
  lastDuration?: number;
  lastError?: string;
  environmentState?: any;
  fetchIds?: any;
  startType?: string;
  originalRequest?: any;
  originalHeaders?: any;
  originalUrl?: string;
  originalRequestUrl?: string;
  runs?: {
    edges: Array<{ node: TestRun }>;
  };
  selectedRun?: {
    edges: Array<{ node: TestRun }>;
  };
}

interface SnapshotValidationContainerProps extends InjectedIntlProps, WithRouterProps {
  testInvocation: TestInvocation;
  params: {
    organisationId: string;
    flowId: string;
    id?: string;
    runId?: string;
  };
  relay: any;
}

interface SnapshotValidationContainerState {
  expandedGroupIndices: Set<number>;
  diffsCommand: { action: string; id: number } | null;
  conflictError: string | null;
}

export function normalizeSnapshotEntityId(id?: string | null): string | null {
  if (!id) {
    return null;
  }

  if (uuidRegex.test(id)) {
    return id;
  }

  try {
    const globalId = fromGlobalId(id);
    return globalId.id || id;
  } catch (e) {
    return id;
  }
}

export function getPreferredSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null): string | null {
  if (!entity) {
    return null;
  }

  return entity.realId || normalizeSnapshotEntityId(entity.id);
}

export function isSameSnapshotEntityId(entity?: { id?: string | null; realId?: string | null } | null, otherId?: string | null): boolean {
  const normalizedEntityId = getPreferredSnapshotEntityId(entity);
  const normalizedOtherId = normalizeSnapshotEntityId(otherId);

  return !!normalizedEntityId && normalizedEntityId === normalizedOtherId;
}

export class SnapshotValidationContainer extends React.Component<SnapshotValidationContainerProps, SnapshotValidationContainerState> {
  constructor(props: SnapshotValidationContainerProps) {
    super(props);
    this.state = {
      expandedGroupIndices: new Set(),
      diffsCommand: null,
      conflictError: null,
    };
  }

  getSelectedRun() {
    const { testInvocation, params } = this.props;
    const runs = testInvocation?.runs?.edges ? testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const selectedRuns = testInvocation?.selectedRun?.edges ? testInvocation.selectedRun.edges.map(e => e?.node).filter(Boolean) : [];

    if (!runs.length) {
      return selectedRuns[0] || null;
    }

    if (params.runId) {
      const selectedRun = selectedRuns.find(r => r && isSameSnapshotEntityId(r, params.runId));
      if (selectedRun) {
        return selectedRun;
      }

      return runs.find(r => r && isSameSnapshotEntityId(r, params.runId)) || runs[0];
    }

    return selectedRuns[0] || runs[0];
  }

  refreshViaCanonicalRoute = () => {
    const { organisationId, flowId, runId } = this.props.params;
    const invocationId = getPreferredSnapshotEntityId(this.props.testInvocation) || this.props.params.id;

    const url = runId
      ? urlMapping.getUrl('snapshotTesting', 'validateRun', {
        organisation_id: organisationId,
        flow_id: flowId,
        id: invocationId,
        runId: normalizeSnapshotEntityId(runId),
      })
      : urlMapping.getUrl('snapshotTesting', 'validate', {
        organisation_id: organisationId,
        flow_id: flowId,
        id: invocationId,
      });

    this.props.router.replace(url);
  };

  handleAccept = () => {
    const lastRun = this.getSelectedRun();

    if (!lastRun) return;

    this.setState({ conflictError: null });

    Relay.Store.commitUpdate(
      new AcceptSnapshotMutation({
        testInvocation: this.props.testInvocation,
        testRunId: getPreferredSnapshotEntityId(lastRun) || undefined,
      }),
      {
        onSuccess: (response: any) => {
          if (response.acceptTestInvocationSnapshot && response.acceptTestInvocationSnapshot.conflict) {
            this.setState({
              conflictError: response.acceptTestInvocationSnapshot.error || this.props.intl.formatMessage(messages.conflictDetected)
            });
            this.refreshViaCanonicalRoute();
            return;
          }
          const payloadError = getPayloadErrorMessage(
            response.acceptTestInvocationSnapshot,
            this.props.intl.formatMessage(messages.acceptFailed)
          );
          if (payloadError) {
            this.setState({ conflictError: payloadError });
            return;
          }

          this.refreshViaCanonicalRoute();
        },
        onFailure: () => {
          this.setState({ conflictError: this.props.intl.formatMessage(messages.acceptFailed) });
        }
      }
    );
  };

  handleManualUpdate = (expectedOutput: any) => {
    this.setState({ conflictError: null });

    Relay.Store.commitUpdate(
      new SetExpectedOutputMutation({
        testInvocation: this.props.testInvocation,
        expectedOutput: expectedOutput,
      }),
      {
        onSuccess: (response: any) => {
          if (response.setExpectedOutput && response.setExpectedOutput.conflict) {
            this.setState({
              conflictError: response.setExpectedOutput.error || this.props.intl.formatMessage(messages.conflictDetected)
            });
            this.refreshViaCanonicalRoute();
            return;
          }
          const payloadError = getPayloadErrorMessage(
            response.setExpectedOutput,
            this.props.intl.formatMessage(messages.setExpectedOutputFailed)
          );
          if (payloadError) {
            this.setState({ conflictError: payloadError });
            return;
          }

          this.refreshViaCanonicalRoute();
        },
        onFailure: () => {
          this.setState({ conflictError: this.props.intl.formatMessage(messages.setExpectedOutputFailed) });
        }
      }
    );
  };

  handleEditEnvironmentState = (environmentStateJson: string) => {
    this.setState({ conflictError: null });

    Relay.Store.commitUpdate(
      new UpdateEnvironmentStateMutation({
        testInvocation: this.props.testInvocation,
        environmentState: environmentStateJson,
      }),
      {
        onSuccess: (response: any) => {
          if (response.updateTestInvocationEnvironmentState && response.updateTestInvocationEnvironmentState.conflict) {
            this.setState({
              conflictError: response.updateTestInvocationEnvironmentState.error || this.props.intl.formatMessage(messages.conflictDetected)
            });
            this.refreshViaCanonicalRoute();
            return;
          }
          const payloadError = getPayloadErrorMessage(
            response.updateTestInvocationEnvironmentState,
            this.props.intl.formatMessage(messages.updateSnapshotFailed)
          );
          if (payloadError) {
            this.setState({ conflictError: payloadError });
            return;
          }
          this.refreshViaCanonicalRoute();
        },
        onFailure: () => {
          this.setState({ conflictError: this.props.intl.formatMessage(messages.updateSnapshotFailed) });
        },
      }
    );
  };

  handleDelete = () => {
    if (!confirm(this.props.intl.formatMessage(messages.confirmDelete))) {
      return;
    }

    Relay.Store.commitUpdate(
      new DeleteTestInvocationMutation({
        testInvocation: this.props.testInvocation,
      }),
      {
        onSuccess: (response: any) => {
          const payloadError = getPayloadErrorMessage(
            response && response.deleteTestInvocation,
            this.props.intl.formatMessage(messages.deleteFailed)
          );
          if (payloadError) {
            notification.error(payloadError);
            return;
          }
          const { organisationId, flowId } = this.props.params;
          const url = urlMapping.getUrl('snapshotManagement', 'manage', {
            organisation_id: organisationId,
            id: flowId
          });
          this.props.router.push(url);
        },
        onFailure: (error: any) => {
          console.error('Failed to delete test invocation', error);
          notification.error(this.props.intl.formatMessage(messages.deleteFailed));
        },
      }
    );
  };

  handleGroupToggle = (index: number) => {
    const newExpanded = new Set(this.state.expandedGroupIndices);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    this.setState({ expandedGroupIndices: newExpanded });
  };

  handleExpandAllGroups = (count: number) => {
    const all = new Set<number>();
    for (let i = 0; i < count; i++) all.add(i);
    this.setState({ expandedGroupIndices: all });
  };

  handleCollapseAllGroups = () => {
    this.setState({ expandedGroupIndices: new Set() });
  };

  handleExpandAllDiffs = () => {
    this.setState({ diffsCommand: { action: 'expand', id: Date.now() } });
  };

  handleCollapseAllDiffs = () => {
    this.setState({ diffsCommand: { action: 'collapse', id: Date.now() } });
  };

  render() {
    const { testInvocation, params } = this.props;

    if (!testInvocation) return null;

    const runs = testInvocation?.runs?.edges ? testInvocation.runs.edges.map(e => e?.node).filter(Boolean) : [];
    const lastRun = this.getSelectedRun();

    const computedDiffPayload = buildDisplayComputedDiffPayload(testInvocation, lastRun);
    const isDiffUnavailable = !!lastRun && !computedDiffPayload;
    const groups = computedDiffPayload?.groups || [];
    const expectedSummary = computedDiffPayload && Object.prototype.hasOwnProperty.call(computedDiffPayload, 'expectedSummary')
      ? computedDiffPayload.expectedSummary
      : null;
    const actualSummary = computedDiffPayload && Object.prototype.hasOwnProperty.call(computedDiffPayload, 'actualSummary')
      ? computedDiffPayload.actualSummary
      : null;
    const hasSummaryMismatch = !!computedDiffPayload?.hasSummaryMismatch;
    const mismatchCount = computedDiffPayload?.mismatchCount || 0;
    const maxWritesLen = computedDiffPayload?.maxWritesLen || 0;

    const isHistoricalRun = !!(lastRun && runs && runs.length > 0 && !isSameSnapshotEntityId(lastRun, getPreferredSnapshotEntityId(runs[0])));

    return (
      <FlowTestResultView
        actualSummary={actualSummary}
        conflictError={this.state.conflictError}
        diffsCommand={this.state.diffsCommand}
        expandedGroupIndices={this.state.expandedGroupIndices}
        expectedSummary={expectedSummary}
        groups={groups}
        hasSummaryMismatch={hasSummaryMismatch}
        isDiffUnavailable={isDiffUnavailable}
        intl={this.props.intl}
        isHistoricalRun={isHistoricalRun}
        lastRun={lastRun}
        maxWritesLen={maxWritesLen}
        mismatchCount={mismatchCount}
        onAccept={this.handleAccept}
        onCollapseAllDiffs={this.handleCollapseAllDiffs}
        onCollapseAllGroups={this.handleCollapseAllGroups}
        onDelete={this.handleDelete}
        onExpandAllDiffs={this.handleExpandAllDiffs}
        onExpandAllGroups={() => this.handleExpandAllGroups(groups.length)}
        onGroupToggle={this.handleGroupToggle}
        onManualUpdate={this.handleManualUpdate}
        onEditEnvironmentState={this.handleEditEnvironmentState}
        params={params}
        testInvocation={testInvocation}
      />
    );
  }
}

export default Relay.createContainer(injectIntl(withRouter(SnapshotValidationContainer)), {
  initialVariables: {
    id: null,
    runId: null,
  },
  fragments: {
    testInvocation: () => Relay.QL`
      fragment on TestInvocation {
        id
        realId
        name
        organisation_id
        flow_id
        flowDef_id
        flowDefVersion_id
        sourceFlowInvocation_id
        expectedOutput
        version
        createdAt
        updatedAt
        lastStatus
        lastRunAt
        lastDuration
        lastError
        environmentState
        fetchIds
        startType
        originalRequest
        originalHeaders
        originalUrl
        originalRequestUrl
        runs(first: 100) {
          edges {
            node {
              id
              realId
              status
              createdAt
              error
              duration
              expiresIn
            }
          }
        }
        selectedRun: runs(first: 1, id: $runId) {
          edges {
            node {
              id
              realId
              status
              createdAt
              actualOutput
              computedDiff
              error
              duration
              expiresIn
            }
          }
        }
      }
    `,
  },
});
